import { computed, ref, onMounted, onUnmounted } from 'vue';

export const Subscription = {
  PRO: 0,
  PLUS: 1,
  BASIC: 2
}

// Global state - created once and shared across all instances
let globalUserData = null;
let globalSubscription = null;
let isInitialized = false;

export const useAuth = () => {
  // Access the injected PocketBase instance
  const { $pb } = useNuxtApp();
  
  // Initialize global state only once
  if (!globalUserData) {
    globalUserData = ref($pb.authStore.model);
  }
  
  // Computed properties based on global userData
  const userEmail = computed(() => $pb.authStore.model?.email || 'user@example.com')
  const isAuthenticated = computed(() => $pb.authStore.isValid);
  const user = computed(() => globalUserData.value);
  const credits = computed(() => {
    if (!globalUserData.value) return 0;
    return globalUserData.value.credits;
  });
  
  const subscriptionType = computed(() => {
    if (!globalUserData.value || !globalUserData.value.subscription) {
      return Subscription.BASIC;
    }
    
    switch (globalUserData.value.subscription.toLowerCase()) {
      case 'plus':
        return Subscription.PLUS;
      case 'pro':
        return Subscription.PRO;
      default:
        return Subscription.BASIC;
    }
  });
  
  // Simple function to refresh user data
  const refreshUserData = async () => {
    if ($pb.authStore.isValid && $pb.authStore.model?.id) {
      try {
        const freshData = await $pb.collection(COLLECTIONS.USERS).getOne($pb.authStore.model.id);
        globalUserData.value = freshData;
      } catch (error) {
        console.error("Failed to refresh user data:", error);
      }
    }
  };
  
  // Initialize only once globally
  const initializeAuth = () => {
    if (isInitialized) return;
    isInitialized = true;
    
    // Initial fetch if user is already authenticated
    if ($pb.authStore.isValid) {
      refreshUserData();
      
      // Set up subscription for the current user
      if ($pb.authStore.model?.id) {
        globalSubscription = $pb.collection(COLLECTIONS.USERS).subscribe($pb.authStore.model.id, (data) => {
          globalUserData.value = data.record;
        });
      }
    }
    
    // Listen for auth changes
    $pb.authStore.onChange(() => {
      // Update user data
      globalUserData.value = $pb.authStore.model;
      
      // Clean up old subscription
      if (globalSubscription) {
        globalSubscription = null;
      }
      
      // Set up new subscription if authenticated
      if ($pb.authStore.isValid && $pb.authStore.model?.id) {
        refreshUserData();
        globalSubscription = $pb.collection(COLLECTIONS.USERS).subscribe($pb.authStore.model.id, (data) => {
          globalUserData.value = data.record;
        });
      }
    });
  };
  
  // Handle mounting/initialization
  onMounted(() => {
    initializeAuth();
  });
  
  // Only clean up on app unmount, not component unmount
  onUnmounted(() => {
    // Don't cleanup global subscription here unless it's the app root
  });

  /**
   * Login with email and password
   */
  const loginWithEmail = async (email, password) => {
    try {
      const authData = await $pb.collection(COLLECTIONS.USERS).authWithPassword(email, password);
      
      if (!authData.record.verified) {
        $pb.authStore.clear();
        throw new Error('Email not verified. Please check your inbox for the verification link.');
      }
      
      return authData;
    } catch (error) {
      console.error("loginWithEmail failed:", error);
      throw error;
    }
  };

  /**
   * Login with Google OAuth2
   */
  const loginWithGoogle = async () => {
    if (!import.meta.client) {
      throw new Error('OAuth can only be performed on the client side');
    }
    try {
      const authData = await $pb.collection(COLLECTIONS.USERS).authWithOAuth2({
        provider: 'google',
      });
      return authData;
    } catch (error) {
      console.error("loginWithGoogle failed:", error);
      throw error;
    }
  };

  /**
   * Register a new user with email
   */
  const registerWithEmail = async (email, password, name) => {
    try {
      const userData = {
        email,
        password,
        passwordConfirm: password,
        name,
        verified: false
      };
      
      const record = await $pb.collection(COLLECTIONS.USERS).create(userData);
      await $pb.collection(COLLECTIONS.USERS).requestVerification(email);
      
      return record;
    } catch (error) {
      console.error("registerWithEmail failed:", error);
      throw error;
    }
  };

  /**
   * Verify a user's email with the token received via email
   */
  const verifyEmail = async (token) => {
    try {
      return await $pb.collection(COLLECTIONS.USERS).confirmVerification(token);
    } catch (error) {
      console.error("verifyEmail failed:", error);
      throw error;
    }
  };

  /**
   * Send a password reset email
   */
  const sendPasswordResetEmail = async (email) => {
    try {
      return await $pb.collection(COLLECTIONS.USERS).requestPasswordReset(email);
    } catch (error) {
      console.error("sendPasswordResetEmail failed:", error);
      throw error;
    }
  };

  /**
   * Reset password using the token received via email
   */
  const resetPassword = async (token, newPassword) => {
    try {
      return await $pb.collection(COLLECTIONS.USERS).confirmPasswordReset(
        token,
        newPassword,
        newPassword
      );
    } catch (error) {
      console.error("resetPassword failed:", error);
      throw error;
    }
  };

  /**
   * Resend verification email
   */
  const resendVerificationEmail = async (email) => {
    try {
      return await $pb.collection(COLLECTIONS.USERS).requestVerification(email);
    } catch (error) {
      console.error("resendVerificationEmail failed:", error);
      throw error;
    }
  };

  /**
   * Logout the current user
   */
  const logout = () => {
    if (globalSubscription) {
      globalSubscription = null;
    }
    $pb.authStore.clear();
    globalUserData.value = null;
  };

  const refreshUser = () => refreshUserData();

  return {
    isAuthenticated,
    user,
    userEmail,
    credits,
    subscriptionType,
    loginWithEmail,
    loginWithGoogle,
    registerWithEmail,
    verifyEmail,
    sendPasswordResetEmail,
    resetPassword,
    resendVerificationEmail,
    logout,
    refreshUser,
  };
};